import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { BUTTON_BASE, BUTTON_SIZES, BUTTON_VARIANTS } from '../components/buttonStyles.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type {
  CompanyDocument,
  Question,
  QuestionCategory,
  Questionnaire,
  QuestionType,
} from '../lib/types.ts';
import {
  draftToVisibilityPayload,
  emptyCond,
  emptyGroup,
  questionToRuleDraft,
  type CondDraft,
  type GroupDraft,
} from '../lib/visibilityRuleDraft.ts';
import { questionsDeletedByRemoving } from './categoryRemoval.ts';
import { useConfirm } from '../lib/confirm.tsx';

type DraftTrigger = {
  // null = unconditional adoption document (generic questions). The
  // multiselect_uploads option-mapped flow stores its option key here.
  trigger_value: string | null;
  document_id: string;
  adopt_only: boolean;
};

type QuestionDraft = {
  type: QuestionType;
  // The questionnaire this question belongs to (migration 0032). Set from the
  // builder's active questionnaire on create; questions are scoped per
  // questionnaire even when their category is shared.
  questionnaire_id: string;
  category_id: string;
  label: string;
  help_text: string;
  required: boolean;
  requires_upload: boolean;
  requires_expiry: boolean;
  allow_multiple_files: boolean;
  include_in_compliance_documents: boolean;
  compliance_doc_label: string;
  exclude_from_compliance: boolean;
  upload_label: string;
  options: string[];
  visibility: GroupDraft | null;
  adoptable_doc_id: string | null;
  video_url: string;
  image_url: string;
  adoption_triggers: DraftTrigger[];
  no_followup_ids: string[];
  // Per-option follow-ups for multiselect / multiselect_uploads parents.
  // Map from option string → list of child question ids that should appear
  // (and be required) when that option is ticked.
  option_followup_ids: Record<string, string[]>;
};

function emptyDraft(defaultCategoryId: string, questionnaireId: string): QuestionDraft {
  return {
    type: 'yesno',
    questionnaire_id: questionnaireId,
    category_id: defaultCategoryId,
    label: '',
    help_text: '',
    required: true,
    requires_upload: false,
    requires_expiry: false,
    allow_multiple_files: true,
    include_in_compliance_documents: false,
    compliance_doc_label: '',
    exclude_from_compliance: false,
    upload_label: '',
    options: [],
    visibility: null,
    adoptable_doc_id: null,
    video_url: '',
    image_url: '',
    adoption_triggers: [],
    no_followup_ids: [],
    option_followup_ids: {},
  };
}

export function AdminQuestionBuilder() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const questionnaireId = searchParams.get('questionnaireId');

  const questionnairesQuery = useQuery({
    queryKey: ['questionnaires'],
    queryFn: () => adminApi.get<{ questionnaires: Questionnaire[] }>('/api/questionnaires'),
  });

  const questionsQuery = useQuery({
    queryKey: ['questions', questionnaireId ?? 'all'],
    queryFn: () =>
      adminApi.get<{ questions: Question[] }>(
        questionnaireId ? `/api/questions?questionnaire_id=${questionnaireId}` : '/api/questions',
      ),
    enabled: questionnairesQuery.isSuccess,
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['question-categories', questionnaireId ?? 'all'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>(
        questionnaireId
          ? `/api/question-categories?questionnaire_id=${questionnaireId}`
          : '/api/question-categories',
      ),
    enabled: questionnairesQuery.isSuccess,
  });
  // The full global category list, used to offer categories not yet linked to
  // this questionnaire (adding one links it via the questionnaire's join).
  const allCategoriesQuery = useQuery({
    queryKey: ['question-categories', 'all'],
    queryFn: () => adminApi.get<{ categories: QuestionCategory[] }>('/api/question-categories'),
    enabled: questionnairesQuery.isSuccess,
  });

  const [editing, setEditing] = useState<Question | null>(null);
  const [creatingInCategory, setCreatingInCategory] = useState<string | null>(null);
  const [optimisticOrder, setOptimisticOrder] = useState<string[] | null>(null);

  const questions = (questionsQuery.data?.questions ?? []).filter((q) => !q.deleted_at);
  const documents = documentsQuery.data?.documents ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const allCategories = allCategoriesQuery.data?.categories ?? [];
  // Global categories not yet linked to this questionnaire — the ones the
  // "Add category" picker offers.
  const availableCategories = allCategories.filter(
    (c) => !categories.some((linked) => linked.id === c.id),
  );

  const ordered = useMemo(() => {
    if (!optimisticOrder) return questions;
    const map = new Map(questions.map((q) => [q.id, q]));
    return optimisticOrder.map((id) => map.get(id)!).filter(Boolean);
  }, [questions, optimisticOrder]);

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.post('/api/questions/reorder', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questions'] });
      setOptimisticOrder(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/questions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['questions'] }),
  });

  // Link an existing global category to this questionnaire by appending it to
  // the questionnaire's category set (the PATCH reconciles the join).
  const addCategoryMutation = useMutation({
    mutationFn: (categoryId: string) =>
      adminApi.patch(`/api/questionnaires/${questionnaireId}`, {
        category_ids: [...categories.map((c) => c.id), categoryId],
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
      void qc.invalidateQueries({ queryKey: ['questionnaires'] });
    },
  });

  // Remove a category from this questionnaire. The PATCH deletes the category's
  // questions in this questionnaire (server-side cascade) and drops the link.
  const removeCategoryMutation = useMutation({
    mutationFn: (categoryId: string) =>
      adminApi.patch(`/api/questionnaires/${questionnaireId}`, {
        category_ids: categories.map((c) => c.id).filter((id) => id !== categoryId),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questions'] });
      void qc.invalidateQueries({ queryKey: ['question-categories'] });
      void qc.invalidateQueries({ queryKey: ['questionnaires'] });
    },
  });

  function moveWithinCategory(categoryId: string, id: string, dir: -1 | 1) {
    const inCategory = ordered
      .filter((q) => q.category_id === categoryId && !q.instance_of)
      .map((q) => q.id);
    const idx = inCategory.indexOf(id);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= inCategory.length) return;
    const swapped = [...inCategory];
    [swapped[idx], swapped[next]] = [swapped[next]!, swapped[idx]!];
    // Splice back into the full ordering so display_order changes only within the category.
    const fullIds = ordered.map((q) => q.id);
    let j = 0;
    const merged = fullIds.map((qid) => {
      const q = questions.find((x) => x.id === qid)!;
      if (q.category_id === categoryId && !q.instance_of) return swapped[j++]!;
      return qid;
    });
    setOptimisticOrder(merged);
    reorderMutation.mutate(merged);
  }

  const questionnaires = questionnairesQuery.data?.questionnaires ?? [];
  const activeQuestionnaire = questionnaires.find((q) => q.id === questionnaireId) ?? null;

  if (
    questionnairesQuery.isLoading ||
    questionsQuery.isLoading ||
    documentsQuery.isLoading ||
    categoriesQuery.isLoading ||
    allCategoriesQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }

  if (questionnaires.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No questionnaires yet"
          description="Create a questionnaire first on the Questionnaires page, then come back here to define its questions."
        />
        <Link to="/admin/questionnaires" className="text-sm text-[#4D148C] hover:underline">
          → Go to Questionnaires
        </Link>
      </div>
    );
  }

  if (!activeQuestionnaire) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Question builder</h1>
          <p className="mt-1 text-sm text-slate-500">
            Pick a questionnaire to edit its categories and questions.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <ul className="divide-y divide-slate-100">
            {questionnaires.map((q) => (
              <li key={q.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium text-slate-900">{q.name}</div>
                  <div className="text-xs text-slate-500">
                    Audience: {q.audience === 'company' ? 'vendor' : q.audience}
                    {q.contractor_types && q.contractor_types.length > 0
                      ? ` · Contractor types: ${q.contractor_types.join(', ')}`
                      : ' · All contractor types'}
                  </div>
                </div>
                <Btn
                  size="sm"
                  variant="secondary"
                  onClick={() => setSearchParams({ questionnaireId: q.id })}
                >
                  Edit questions →
                </Btn>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="space-y-4">
        <QuestionnaireHeader
          questionnaires={questionnaires}
          active={activeQuestionnaire}
          onSelect={(id) => setSearchParams({ questionnaireId: id })}
        />
        <EmptyState
          title="No categories in this questionnaire yet"
          description="Add an existing category below to start building this questionnaire's questions."
        />
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <AddCategoryToQuestionnaire
            available={availableCategories}
            hasGlobalCategories={allCategories.length > 0}
            pending={addCategoryMutation.isPending}
            onAdd={(id) => addCategoryMutation.mutate(id)}
          />
        </div>
      </div>
    );
  }

  const grouped = new Map<string, Question[]>();
  for (const cat of categories) grouped.set(cat.id, []);
  for (const q of ordered) {
    if (q.instance_of) continue; // mirrors are managed via their authored question
    const list = grouped.get(q.category_id);
    if (list) list.push(q);
  }

  return (
    <div className="space-y-8">
      <QuestionnaireHeader
        questionnaires={questionnaires}
        active={activeQuestionnaire}
        onSelect={(id) => setSearchParams({ questionnaireId: id })}
      />
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">
            {questions.filter((x) => !x.instance_of).length} question
            {questions.filter((x) => !x.instance_of).length === 1 ? '' : 's'} across{' '}
            {categories.length} categor{categories.length === 1 ? 'y' : 'ies'}
          </p>
        </div>
        <AddCategoryToQuestionnaire
          available={availableCategories}
          hasGlobalCategories={allCategories.length > 0}
          pending={addCategoryMutation.isPending}
          onAdd={(id) => addCategoryMutation.mutate(id)}
        />
      </div>

      {categories.map((cat) => {
        const list = grouped.get(cat.id) ?? [];
        return (
          <section key={cat.id} className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">{cat.name}</h2>
              <div className="flex items-center gap-2">
                <Btn size="sm" onClick={() => setCreatingInCategory(cat.id)}>
                  + New question
                </Btn>
                <Btn
                  size="sm"
                  variant="ghost"
                  loading={
                    removeCategoryMutation.isPending && removeCategoryMutation.variables === cat.id
                  }
                  onClick={() => {
                    void (async () => {
                      // Authored (non-mirror) questions that will be deleted;
                      // mirrors share a parent's label and are noise in the list.
                      const doomed = questionsDeletedByRemoving(questions, cat.id).filter(
                        (q) => !q.instance_of,
                      );
                      const ok = await confirm({
                        title: 'Remove category',
                        body: (
                          <div className="space-y-2">
                            <p>
                              Remove <span className="font-semibold">{cat.name}</span> from this
                              questionnaire?
                            </p>
                            {doomed.length > 0 && (
                              <>
                                <p>
                                  This permanently deletes {doomed.length} question
                                  {doomed.length === 1 ? '' : 's'} and any answers/files vendors
                                  submitted for them:
                                </p>
                                <ul className="list-disc pl-5">
                                  {doomed.map((q) => (
                                    <li key={q.id}>{q.label}</li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </div>
                        ),
                        confirmLabel: 'Remove',
                        danger: true,
                      });
                      if (ok) removeCategoryMutation.mutate(cat.id);
                    })();
                  }}
                >
                  Remove
                </Btn>
              </div>
            </div>
            {list.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-500">
                No questions in this category yet.
              </div>
            ) : (
              <div className="space-y-2">
                {list.map((q, i) => (
                  <QuestionRow
                    key={q.id}
                    question={q}
                    index={i}
                    total={list.length}
                    onUp={() => moveWithinCategory(cat.id, q.id, -1)}
                    onDown={() => moveWithinCategory(cat.id, q.id, 1)}
                    onEdit={() => setEditing(q)}
                    onDelete={() => {
                      void (async () => {
                        const children = questions.filter((c) => c.parent_id === q.id);
                        // Collapse mirror rows into their authored row so a
                        // follow-up linked to N options is listed once, not N times.
                        const distinctChildren = [
                          ...new Map(children.map((c) => [c.instance_of ?? c.id, c])).values(),
                        ];
                        // Mirrors of THIS question (q is an authored follow-up shown
                        // for several options); deleting q cascade-deletes them and
                        // any answers companies gave for those options.
                        const mirrors = questions.filter((c) => c.instance_of === q.id);
                        const parts: string[] = [];
                        if (distinctChildren.length > 0) {
                          parts.push(
                            `${distinctChildren.length} dependent question${distinctChildren.length === 1 ? '' : 's'} reference${distinctChildren.length === 1 ? 's' : ''} this as a parent and will become orphaned (never visible):\n${distinctChildren.map((c) => `  • ${c.label}`).join('\n')}`,
                          );
                        }
                        if (mirrors.length > 0) {
                          parts.push(
                            `This question is shown for ${mirrors.length + 1} options of a multi-select. Deleting it removes all ${mirrors.length + 1} option instances and any answers vendors have already given for them.`,
                          );
                        }
                        const ok = await confirm({
                          title: 'Delete question',
                          body: (
                            <div className="space-y-2">
                              <p>
                                Delete <span className="font-semibold">{q.label}</span>?
                              </p>
                              {parts.length > 0 && (
                                <div className="whitespace-pre-line">{parts.join('\n\n')}</div>
                              )}
                            </div>
                          ),
                          confirmLabel: 'Delete',
                          danger: true,
                        });
                        if (ok) deleteMutation.mutate(q.id);
                      })();
                    }}
                    parents={questions}
                    documents={documents}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}

      {creatingInCategory && (
        <QuestionEditor
          mode="create"
          defaultCategoryId={creatingInCategory}
          questionnaireId={activeQuestionnaire.id}
          questions={questions}
          categories={categories}
          documents={documents}
          onClose={() => setCreatingInCategory(null)}
        />
      )}
      {editing && (
        <QuestionEditor
          mode="edit"
          existing={editing}
          defaultCategoryId={editing.category_id}
          questionnaireId={activeQuestionnaire.id}
          questions={questions}
          categories={categories}
          documents={documents}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function QuestionRow({
  question,
  index,
  total,
  onUp,
  onDown,
  onEdit,
  onDelete,
  parents,
  documents,
}: {
  question: Question;
  index: number;
  total: number;
  onUp: () => void;
  onDown: () => void;
  onEdit: () => void;
  onDelete: () => void;
  parents: Question[];
  documents: CompanyDocument[];
}) {
  const parent = parents.find((p) => p.id === question.parent_id);
  const adoptable = documents.find((d) => d.id === question.adoptable_doc_id);
  const noFollowups =
    question.type === 'yesno'
      ? parents.filter((c) => c.parent_id === question.id && c.condition?.value === false)
      : [];
  const optionFollowups =
    question.type === 'multiselect' || question.type === 'multiselect_uploads'
      ? parents.filter(
          (c) =>
            c.parent_id === question.id &&
            typeof c.condition?.value === 'string' &&
            (question.options ?? []).includes(c.condition.value),
        )
      : [];
  const optionFollowupCount = new Set(optionFollowups.map((c) => c.instance_of ?? c.id)).size;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="flex flex-col">
          <button
            onClick={onUp}
            disabled={index === 0}
            className="px-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
            aria-label="Move up"
          >
            ▲
          </button>
          <button
            onClick={onDown}
            disabled={index === total - 1}
            className="px-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"
            aria-label="Move down"
          >
            ▼
          </button>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-slate-500">{question.type}</span>
            {question.required && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                required
              </span>
            )}
            {question.requires_upload && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                upload
              </span>
            )}
            {parent && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                if “{parent.label}” = {linkedOptionsLabel(question, parents)}
              </span>
            )}
            {question.visibility_rule && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                conditional
              </span>
            )}
            {noFollowups.length > 0 && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                {noFollowups.length} follow-up{noFollowups.length === 1 ? '' : 's'} on “No”
              </span>
            )}
            {optionFollowupCount > 0 && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                {optionFollowupCount} option follow-up
                {optionFollowupCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm font-medium text-slate-900">{question.label}</div>
          {question.help_text && (
            <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
          )}
          {question.options && question.options.length > 0 && (
            <div className="mt-1 text-xs text-slate-500">
              Options: {question.options.join(', ')}
            </div>
          )}
          {adoptable && (
            <div className="mt-1 text-xs text-slate-500">
              {question.type === 'document_upload' ? 'Adoptable alternative: ' : 'Adopt: '}
              {adoptable.name}
            </div>
          )}
          {question.type === 'informational' && question.video_url && (
            <div className="mt-1 text-xs text-slate-500 break-all">
              🎬 Video: {question.video_url}
            </div>
          )}
          {question.type === 'image_info' && question.image_url && (
            <div className="mt-2 flex items-center gap-2">
              <img
                src={question.image_url}
                alt=""
                className="h-12 w-auto rounded border border-slate-200 object-contain"
              />
              <span className="text-xs text-slate-500 break-all">🖼 {question.image_url}</span>
            </div>
          )}
          {question.adoption_triggers.length > 0 && (
            <div className="mt-1 text-xs text-slate-500">
              Triggers:{' '}
              {question.adoption_triggers
                .map(
                  (t) =>
                    `${t.trigger_value ?? 'Always'} → ${documents.find((d) => d.id === t.document_id)?.name ?? '?'}${t.adopt_only ? ' (adopt only)' : ''}`,
                )
                .join(' · ')}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Btn size="sm" variant="secondary" onClick={onEdit}>
            Edit
          </Btn>
          <Btn size="sm" variant="ghost" onClick={onDelete}>
            Delete
          </Btn>
        </div>
      </div>
    </div>
  );
}

// "One option per line" editor. The textarea is driven by its own RAW text
// state (not by the normalised draft.options), so a freshly-typed newline — and
// any blank in-progress line — survives instead of being stripped on the same
// keystroke. Each change still emits a trimmed, blank-free array to the parent
// draft, so all downstream readers (validation, per-option follow-ups, save)
// keep seeing clean options. (Mirrors the MultiselectUploadsOptionsEditor
// initialOptions/onChange pattern.)
function OptionsTextArea({
  initialOptions,
  onChange,
}: {
  initialOptions: string[];
  onChange: (options: string[]) => void;
}) {
  const [text, setText] = useState(initialOptions.join('\n'));
  return (
    <Field label="Options" required hint="One per line">
      <TextArea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(
            e.target.value
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean),
          );
        }}
      />
    </Field>
  );
}

function QuestionEditor({
  mode,
  existing,
  defaultCategoryId,
  questionnaireId,
  questions,
  categories,
  documents,
  onClose,
}: {
  mode: 'create' | 'edit';
  existing?: Question;
  defaultCategoryId: string;
  questionnaireId: string;
  questions: Question[];
  categories: QuestionCategory[];
  documents: CompanyDocument[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [draft, setDraft] = useState<QuestionDraft>(() =>
    fromQuestion(existing, defaultCategoryId, questions, questionnaireId),
  );
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    setDraft(fromQuestion(existing, defaultCategoryId, questions, questionnaireId));
  }, [existing, defaultCategoryId, questions, questionnaireId]);

  const adoptableDocs = documents.filter((d) => d.is_adoptable && d.current_version);
  // Questions that already have per-option mirrors cannot be parents (v1
  // no-nesting rule, enforced by the API); mirrors are never directly linkable.
  const mirroredIds = useMemo(
    () => new Set(questions.filter((x) => x.instance_of).map((x) => x.instance_of!)),
    [questions],
  );
  const possibleParents = questions.filter(
    (q) => q.id !== existing?.id && !q.instance_of && !mirroredIds.has(q.id),
  );
  const possibleFollowups = questions.filter((q) => q.id !== existing?.id && !q.instance_of);
  // Per-option follow-ups cannot have children of their own (v1), so questions
  // that are already parents are not option-followup candidates.
  const existingParentIds = useMemo(
    () => new Set(questions.filter((x) => x.parent_id).map((x) => x.parent_id!)),
    [questions],
  );
  const followupsByCategory = useMemo(() => {
    const groups = new Map<string, Question[]>();
    for (const q of possibleFollowups) {
      const list = groups.get(q.category_id) ?? [];
      list.push(q);
      groups.set(q.category_id, list);
    }
    return groups;
  }, [possibleFollowups]);
  const previousFollowupIds = useMemo(
    () =>
      existing
        ? questions
            .filter((c) => c.parent_id === existing.id && c.condition?.value === false)
            .map((c) => c.id)
        : [],
    [questions, existing],
  );
  // authoredChildId → options it is currently linked to (authored row's own
  // option plus one per mirror), for multiselect parents.
  const previousOptionFollowups = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!existing) return map;
    if (existing.type !== 'multiselect' && existing.type !== 'multiselect_uploads') {
      return map;
    }
    for (const c of questions) {
      if (c.parent_id !== existing.id) continue;
      if (typeof c.condition?.value !== 'string') continue;
      const authoredId = c.instance_of ?? c.id;
      const list = map.get(authoredId) ?? [];
      if (!list.includes(c.condition.value)) list.push(c.condition.value);
      map.set(authoredId, list);
    }
    return map;
  }, [questions, existing]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toApiPayload(draft);
      const saved =
        mode === 'create'
          ? await adminApi.post<{ question: Question }>('/api/questions', payload)
          : await adminApi.patch<{ question: Question }>(`/api/questions/${existing!.id}`, payload);

      if (draft.type === 'yesno') {
        const parentId = saved.question.id;
        const desired = new Set(draft.no_followup_ids);
        const previous = new Set(previousFollowupIds);

        const toLink = draft.no_followup_ids.filter((id) => !previous.has(id));
        const toUnlink = previousFollowupIds.filter((id) => !desired.has(id));

        await Promise.all([
          ...toLink.map((id) =>
            adminApi.patch(`/api/questions/${id}`, {
              parent_id: parentId,
              condition: { value: false },
              required: true,
            }),
          ),
          ...toUnlink.map((id) =>
            adminApi.patch(`/api/questions/${id}`, {
              parent_id: null,
              condition: null,
            }),
          ),
        ]);
      }

      if (draft.type === 'multiselect' || draft.type === 'multiselect_uploads') {
        const parentId = saved.question.id;
        const savedOptions = new Set(saved.question.options ?? []);
        // authoredChildId → desired options (only options that survived the save)
        const desired = new Map<string, string[]>();
        for (const [opt, ids] of Object.entries(draft.option_followup_ids)) {
          if (!savedOptions.has(opt)) continue;
          for (const id of ids) {
            const list = desired.get(id) ?? [];
            if (!list.includes(opt)) list.push(opt);
            desired.set(id, list);
          }
        }

        const childIds = new Set<string>([...previousOptionFollowups.keys(), ...desired.keys()]);
        const ops: Array<Promise<unknown>> = [];
        for (const childId of childIds) {
          const prev = previousOptionFollowups.get(childId) ?? [];
          const next = desired.get(childId) ?? [];
          const unchanged = prev.length === next.length && prev.every((o) => next.includes(o));
          if (unchanged) continue;
          ops.push(
            adminApi.put(`/api/questions/${childId}/option-links`, {
              parent_id: parentId,
              options: next,
            }),
          );
        }
        await Promise.all(ops);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['questions'] });
      onClose();
    },
    onError: (err) => setError(err),
  });

  const requiresOptions =
    draft.type === 'select' || draft.type === 'multiselect' || draft.type === 'multiselect_uploads';
  const isLegacyAdoptType = draft.type === 'document_adopt';
  const isDocumentUpload = draft.type === 'document_upload';
  const isMultiselectUploads = draft.type === 'multiselect_uploads';
  const isSignature = draft.type === 'signature';
  const isInformational = draft.type === 'informational';
  const isImageInfo = draft.type === 'image_info';
  const isReadOnly = isInformational || isImageInfo;
  const isAnyDocType = isLegacyAdoptType || isDocumentUpload;

  function setField<K extends keyof QuestionDraft>(key: K, value: QuestionDraft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  // Unlinking an option deletes its mirror row and (by FK cascade) any company
  // answers already given for it. Surface that before saving.
  function unlinkWarning(): string | null {
    if (draft.type !== 'multiselect' && draft.type !== 'multiselect_uploads') return null;
    const desired = new Map<string, string[]>();
    for (const [opt, ids] of Object.entries(draft.option_followup_ids)) {
      if (!draft.options.includes(opt)) continue;
      for (const id of ids) {
        const list = desired.get(id) ?? [];
        list.push(opt);
        desired.set(id, list);
      }
    }
    const lost: string[] = [];
    for (const [childId, prevOptions] of previousOptionFollowups) {
      const nextOptions = desired.get(childId) ?? [];
      for (const opt of prevOptions) {
        if (!nextOptions.includes(opt)) {
          const child = questions.find((x) => x.id === childId);
          lost.push(`• "${child?.label ?? childId}" for option "${opt}"`);
        }
      }
    }
    if (lost.length === 0) return null;
    return `Unlinking these follow-ups deletes any answers vendors have already given for them:\n\n${lost.join('\n')}`;
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={mode === 'create' ? 'New question' : 'Edit question'}
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={() => {
              void (async () => {
                const warning = unlinkWarning();
                if (warning) {
                  const ok = await confirm({
                    title: 'Unlink follow-ups',
                    body: <div className="whitespace-pre-line">{warning}</div>,
                    confirmLabel: 'Save',
                    danger: true,
                  });
                  if (!ok) return;
                }
                saveMutation.mutate();
              })();
            }}
            loading={saveMutation.isPending}
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Category" required>
            <Select
              value={draft.category_id}
              onChange={(e) => setField('category_id', e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Type" required>
            <Select
              value={draft.type}
              onChange={(e) => setField('type', e.target.value as QuestionType)}
            >
              <option value="yesno">Yes / No</option>
              <option value="text">Text</option>
              <option value="select">Single select</option>
              <option value="multiselect">Multi select</option>
              <option value="multiselect_uploads">Multi select with uploads</option>
              <option value="document_upload">Document upload</option>
              <option value="signature">Signature</option>
              <option value="informational">Informational (no answer)</option>
              <option value="image_info">Image + text (no answer)</option>
              {isLegacyAdoptType && (
                <option value="document_adopt">Document adoption (legacy)</option>
              )}
            </Select>
          </Field>
        </div>

        {!isImageInfo && (
          <Field label="Required">
            <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.required}
                onChange={(e) => setField('required', e.target.checked)}
              />
              {isInformational
                ? 'Vendor must watch the video before they can move on'
                : 'Must be answered'}
            </label>
          </Field>
        )}

        <Field label="Compliance Documents">
          <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.include_in_compliance_documents}
              onChange={(e) => setField('include_in_compliance_documents', e.target.checked)}
            />
            Include this in Compliance Documents
          </label>
        </Field>

        {draft.include_in_compliance_documents && (
          <Field
            label="Compliance Document Label"
            hint="Shown as the document's name in the Compliance Documents tab. Leave blank to use the question title."
          >
            <TextInput
              value={draft.compliance_doc_label}
              onChange={(e) => setField('compliance_doc_label', e.target.value)}
            />
          </Field>
        )}

        <Field label="Compliance progress">
          <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.exclude_from_compliance}
              onChange={(e) => setField('exclude_from_compliance', e.target.checked)}
            />
            Exclude from compliance (no review decision needed; left out of all
            compliance percentages)
          </label>
        </Field>

        <Field label="Label" required>
          <TextInput value={draft.label} onChange={(e) => setField('label', e.target.value)} />
        </Field>

        <Field
          label={isReadOnly ? 'Body text' : 'Help text'}
          hint={
            isReadOnly
              ? 'Rendered as the body of the step. Plain text; line breaks are preserved.'
              : undefined
          }
        >
          <TextArea
            value={draft.help_text}
            onChange={(e) => setField('help_text', e.target.value)}
            rows={isReadOnly ? 5 : 2}
          />
        </Field>

        {isInformational && (
          <Field
            label="Video URL (optional)"
            hint="A YouTube/Vimeo embed URL or a direct MP4 link. If set and the question is required, the vendor must mark it as watched before moving to the next step."
          >
            <TextInput
              type="url"
              placeholder="https://www.youtube.com/embed/..."
              value={draft.video_url}
              onChange={(e) => setField('video_url', e.target.value)}
            />
          </Field>
        )}

        {isImageInfo && (
          <ImageInfoUploader
            imageUrl={draft.image_url}
            onChange={(url) => setField('image_url', url)}
          />
        )}

        {requiresOptions && !isMultiselectUploads && !isReadOnly && (
          <OptionsTextArea
            initialOptions={draft.options}
            onChange={(options) => setField('options', options)}
          />
        )}

        {isMultiselectUploads && (
          <MultiselectUploadsOptionsEditor
            initialOptions={draft.options}
            initialTriggers={draft.adoption_triggers}
            adoptableDocs={adoptableDocs}
            onChange={({ options, triggers }) => {
              setDraft((d) => ({ ...d, options, adoption_triggers: triggers }));
            }}
          />
        )}

        {isDocumentUpload && (
          <Field
            label="Adoptable document (optional)"
            hint="If set, the vendor may adopt this standard policy instead of uploading their own."
          >
            <Select
              value={draft.adoptable_doc_id ?? ''}
              onChange={(e) => setField('adoptable_doc_id', e.target.value || null)}
            >
              <option value="">— None —</option>
              {adoptableDocs.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.version})
                </option>
              ))}
            </Select>
          </Field>
        )}

        {isLegacyAdoptType && (
          <Field label="Adoptable document" required hint="Legacy document adoption type">
            <Select
              value={draft.adoptable_doc_id ?? ''}
              onChange={(e) => setField('adoptable_doc_id', e.target.value || null)}
            >
              <option value="">— Select document —</option>
              {adoptableDocs.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} ({d.version})
                </option>
              ))}
            </Select>
          </Field>
        )}

        {!isAnyDocType && !isSignature && !isReadOnly && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={draft.requires_upload}
                onChange={(e) => setField('requires_upload', e.target.checked)}
              />
              Require file upload
            </div>
            {draft.requires_upload && (
              <>
                <Field label="Upload label" hint="Shown to the vendor above the file drop">
                  <TextInput
                    value={draft.upload_label}
                    onChange={(e) => setField('upload_label', e.target.value)}
                  />
                </Field>
                <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={draft.allow_multiple_files}
                    onChange={(e) => setField('allow_multiple_files', e.target.checked)}
                  />
                  Allow multiple files
                </label>
              </>
            )}
          </div>
        )}

        {(isDocumentUpload || isMultiselectUploads) && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={draft.requires_expiry}
                onChange={(e) => setField('requires_expiry', e.target.checked)}
              />
              Require an expiry date
            </label>
            <p className="mt-1 text-xs text-slate-500">
              The vendor must set an expiry date on the uploaded document before this question is complete.
            </p>
          </div>
        )}

        {isDocumentUpload && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={draft.allow_multiple_files}
                onChange={(e) => setField('allow_multiple_files', e.target.checked)}
              />
              Allow multiple files
            </label>
            <p className="mt-1 text-xs text-slate-500">
              When off, the vendor uploads a single document. To replace it they must remove the
              existing file first.
            </p>
          </div>
        )}

        {!isReadOnly && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="mb-2 text-sm font-semibold text-slate-700">Show only when…</div>
            {draft.visibility ? (
              <RuleGroupEditor
                group={draft.visibility}
                possibleParents={possibleParents}
                categories={categories}
                onChange={(next) => setField('visibility', next)}
              />
            ) : (
              <div>
                <Btn
                  size="sm"
                  variant="secondary"
                  onClick={() => setField('visibility', emptyGroup())}
                >
                  + Add condition
                </Btn>
                <p className="mt-2 text-xs text-slate-500">
                  Always shown. Add conditions to show this question only when other answers match.
                  One condition keeps the question nested under its parent; two or more let it
                  combine answers with ALL/ANY logic.
                </p>
              </div>
            )}
          </div>
        )}

        {draft.type === 'yesno' && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-700">
              Follow-up questions when answered “No”
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Selected questions will only appear when this question is answered No, and will be
              marked as required. Questions from any category can be selected.
            </p>
            {possibleFollowups.length === 0 ? (
              <div className="mt-3 text-xs italic text-slate-500">
                No other questions exist yet. Create them first, then edit this question to link
                them.
              </div>
            ) : (
              <div className="mt-3 max-h-72 space-y-3 overflow-y-auto pr-1">
                {categories.map((cat) => {
                  const list = followupsByCategory.get(cat.id) ?? [];
                  if (list.length === 0) return null;
                  return (
                    <div key={cat.id}>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {cat.name}
                      </div>
                      {list.map((q) => {
                        const checked = draft.no_followup_ids.includes(q.id);
                        const conflict =
                          !checked && q.parent_id !== null && q.parent_id !== existing?.id;
                        const crossCategory = q.category_id !== draft.category_id;
                        return (
                          <label
                            key={q.id}
                            className="flex items-start gap-2 rounded px-2 py-1 text-sm text-slate-700 hover:bg-white"
                          >
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...draft.no_followup_ids, q.id]
                                  : draft.no_followup_ids.filter((id) => id !== q.id);
                                setField('no_followup_ids', next);
                              }}
                            />
                            <span className="flex-1">
                              <span className="font-medium">{q.label}</span>
                              <span className="ml-2 text-xs uppercase tracking-wide text-slate-500">
                                {q.type}
                              </span>
                              {crossCategory && (
                                <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">
                                  cross-category
                                </span>
                              )}
                              {conflict && (
                                <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                                  already linked to another parent — selecting will override
                                </span>
                              )}
                              {!checked && q.visibility_rule !== null && (
                                <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                                  uses advanced conditions — selecting will override
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {(draft.type === 'multiselect' || draft.type === 'multiselect_uploads') && (
          <MultiselectOptionFollowups
            options={draft.options}
            optionFollowupIds={draft.option_followup_ids}
            possibleFollowups={possibleFollowups}
            followupsByCategory={followupsByCategory}
            categories={categories}
            currentCategoryId={draft.category_id}
            existingId={existing?.id ?? null}
            excludedIds={existingParentIds}
            onChange={(next) => setField('option_followup_ids', next)}
          />
        )}

        {!isAnyDocType && !isMultiselectUploads && !isReadOnly && (
          <AdoptionTriggersEditor
            triggers={draft.adoption_triggers}
            adoptableDocs={adoptableDocs}
            onChange={(triggers) => setField('adoption_triggers', triggers)}
            heading="Adoption documents"
            help="Vendors are shown these documents to download, fill in, and upload back with their answers."
          />
        )}

        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}

function MultiselectOptionFollowups({
  options,
  optionFollowupIds,
  possibleFollowups,
  followupsByCategory,
  categories,
  currentCategoryId,
  existingId,
  excludedIds,
  onChange,
}: {
  options: string[];
  optionFollowupIds: Record<string, string[]>;
  possibleFollowups: Question[];
  followupsByCategory: Map<string, Question[]>;
  categories: QuestionCategory[];
  currentCategoryId: string;
  existingId: string | null;
  excludedIds: Set<string>;
  onChange: (next: Record<string, string[]>) => void;
}) {
  const cleanOptions = options.map((o) => o.trim()).filter(Boolean);

  function toggle(option: string, childId: string, checked: boolean) {
    const current = optionFollowupIds[option] ?? [];
    onChange({
      ...optionFollowupIds,
      [option]: checked ? [...current, childId] : current.filter((id) => id !== childId),
    });
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="text-sm font-semibold text-slate-700">Follow-up questions per option</div>
      <p className="mt-1 text-xs text-slate-500">
        For each option, tick the questions that should appear (and be required) when that option is
        selected. The same question can be linked to several options — the vendor answers it once
        per selected option.
      </p>
      {cleanOptions.length === 0 ? (
        <div className="mt-3 text-xs italic text-slate-500">
          Add at least one option above to configure per-option follow-ups.
        </div>
      ) : possibleFollowups.length === 0 ? (
        <div className="mt-3 text-xs italic text-slate-500">
          No other questions exist yet. Create them first, then edit this question to link them.
        </div>
      ) : (
        <div className="mt-3 space-y-4">
          {cleanOptions.map((option) => {
            const checkedIds = new Set(optionFollowupIds[option] ?? []);
            return (
              <div key={option} className="rounded border border-slate-200 bg-white p-3">
                <div className="mb-2 text-sm font-medium text-slate-800">
                  If “{option}” is ticked, show…
                </div>
                <div className="max-h-60 space-y-3 overflow-y-auto pr-1">
                  {categories.map((cat) => {
                    const list = followupsByCategory.get(cat.id) ?? [];
                    if (list.length === 0) return null;
                    return (
                      <div key={cat.id}>
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {cat.name}
                        </div>
                        {list.map((q) => {
                          if (excludedIds.has(q.id)) return null;
                          const checked = checkedIds.has(q.id);
                          const linkedToOtherParent =
                            !checked && q.parent_id !== null && q.parent_id !== existingId;
                          const crossCategory = q.category_id !== currentCategoryId;
                          return (
                            <label
                              key={q.id}
                              className="flex items-start gap-2 rounded px-2 py-1 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={checked}
                                onChange={(e) => toggle(option, q.id, e.target.checked)}
                              />
                              <span className="flex-1">
                                <span className="font-medium">{q.label}</span>
                                <span className="ml-2 text-xs uppercase tracking-wide text-slate-500">
                                  {q.type}
                                </span>
                                {crossCategory && (
                                  <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">
                                    cross-category
                                  </span>
                                )}
                                {linkedToOtherParent && (
                                  <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                                    already linked to another parent — selecting will override
                                  </span>
                                )}
                                {!checked && q.visibility_rule !== null && (
                                  <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
                                    uses advanced conditions — selecting will override
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

type OptionRow = { option: string; document_id: string; adopt_only: boolean };

function MultiselectUploadsOptionsEditor({
  initialOptions,
  initialTriggers,
  adoptableDocs,
  onChange,
}: {
  initialOptions: string[];
  initialTriggers: DraftTrigger[];
  adoptableDocs: CompanyDocument[];
  onChange: (next: { options: string[]; triggers: DraftTrigger[] }) => void;
}) {
  const [rows, setRows] = useState<OptionRow[]>(() => {
    const tByOpt = new Map(initialTriggers.map((t) => [t.trigger_value, t]));
    const base = initialOptions.map((opt) => {
      const t = tByOpt.get(opt);
      return {
        option: opt,
        document_id: t?.document_id ?? '',
        adopt_only: t?.adopt_only ?? false,
      };
    });
    return base.length > 0 ? base : [{ option: '', document_id: '', adopt_only: false }];
  });

  function emit(next: OptionRow[]) {
    setRows(next);
    onChange({
      options: next.map((r) => r.option),
      triggers: next
        .filter((r) => r.option.trim() && r.document_id)
        .map((r) => ({
          trigger_value: r.option,
          document_id: r.document_id,
          adopt_only: r.adopt_only,
        })),
    });
  }

  function update(i: number, patch: Partial<OptionRow>) {
    emit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function remove(i: number) {
    emit(rows.filter((_, idx) => idx !== i));
  }
  function add() {
    emit([...rows, { option: '', document_id: '', adopt_only: false }]);
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">Answer options</div>
        <Btn size="sm" variant="secondary" onClick={add}>
          + Add option
        </Btn>
      </div>
      <p className="mb-3 text-xs text-slate-500">
        Each option is an answer the vendor can select. For every selected answer the vendor gets
        its own document upload zone. Optionally link a standard adoptable document so the vendor
        can adopt it instead of uploading their own.
      </p>
      {rows.length === 0 && <div className="text-xs italic text-slate-500">No options yet.</div>}
      {rows.map((r, i) => (
        <div key={i} className="mb-2 grid grid-cols-12 items-center gap-2">
          <div className="col-span-4">
            <TextInput
              placeholder="Option label"
              value={r.option}
              onChange={(e) => update(i, { option: e.target.value })}
            />
          </div>
          <div className="col-span-5">
            <Select
              value={r.document_id}
              onChange={(e) =>
                update(i, {
                  document_id: e.target.value,
                  adopt_only: e.target.value ? r.adopt_only : false,
                })
              }
            >
              <option value="">— No adoptable doc —</option>
              {adoptableDocs.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </div>
          <label
            className="col-span-2 inline-flex items-center gap-1 text-xs text-slate-700"
            title={r.document_id ? '' : 'Link an adoptable document to enable'}
          >
            <input
              type="checkbox"
              disabled={!r.document_id}
              checked={r.adopt_only}
              onChange={(e) => update(i, { adopt_only: e.target.checked })}
            />
            Adopt only
          </label>
          <div className="col-span-1">
            <Btn size="sm" variant="ghost" onClick={() => remove(i)}>
              ×
            </Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

// Unconditional adoption documents for a generic question: each row links an
// adoptable document the vendor is always shown (to download, fill in and
// upload back). Rows carry no answer condition — trigger_value stays null.
// (The multiselect_uploads per-option mapping lives in a separate editor.)
function AdoptionTriggersEditor({
  triggers,
  adoptableDocs,
  onChange,
  heading,
  help,
}: {
  triggers: DraftTrigger[];
  adoptableDocs: CompanyDocument[];
  onChange: (next: DraftTrigger[]) => void;
  heading: string;
  help: string;
}) {
  const qc = useQueryClient();
  const [uploadError, setUploadError] = useState<unknown>(null);

  // Upload one or more files as adoptable library documents, then append an
  // unconditional row per created doc so it is immediately linked.
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const created: CompanyDocument[] = [];
      for (const file of files) {
        const presigned = await adminApi.post<{ path: string; url: string }>(
          '/api/documents/upload-url',
          { file_name: file.name, mime_type: file.type || 'application/octet-stream' },
        );
        await uploadFileToSignedUrl(presigned.url, file);
        const { document } = await adminApi.post<{ document: CompanyDocument }>('/api/documents', {
          name: file.name,
          category: 'General',
          version: '1.0',
          file_path: presigned.path,
          is_adoptable: true,
        });
        created.push(document);
      }
      return created;
    },
    onSuccess: (created) => {
      setUploadError(null);
      void qc.invalidateQueries({ queryKey: ['documents'] });
      onChange([
        ...triggers,
        ...created.map((doc) => ({
          trigger_value: null,
          document_id: doc.id,
          adopt_only: false,
        })),
      ]);
    },
    onError: (err) => setUploadError(err),
  });

  function add() {
    onChange([...triggers, { trigger_value: null, document_id: '', adopt_only: false }]);
  }
  function update(i: number, patch: Partial<DraftTrigger>) {
    onChange(triggers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  }
  function remove(i: number) {
    onChange(triggers.filter((_, idx) => idx !== i));
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">{heading}</div>
        <div className="flex items-center gap-2">
          <label
            className={`${BUTTON_BASE} ${BUTTON_SIZES.sm} ${BUTTON_VARIANTS.secondary} cursor-pointer`}
          >
            {uploadMutation.isPending ? 'Uploading…' : 'Upload documents'}
            <input
              type="file"
              multiple
              hidden
              disabled={uploadMutation.isPending}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length > 0) uploadMutation.mutate(files);
                e.target.value = '';
              }}
            />
          </label>
          <Btn size="sm" variant="secondary" onClick={add}>
            + Add
          </Btn>
        </div>
      </div>
      <p className="mb-3 text-xs text-slate-500">{help}</p>
      <ErrorMessage error={uploadError} />
      {triggers.length === 0 && (
        <div className="text-xs italic text-slate-500">None configured.</div>
      )}
      {triggers.map((t, i) => (
        <div key={i} className="mb-2 grid grid-cols-12 gap-2">
          <div className="col-span-8">
            <Select
              value={t.document_id}
              onChange={(e) => update(i, { document_id: e.target.value })}
            >
              <option value="">— Select document —</option>
              {adoptableDocs.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </div>
          <label className="col-span-3 inline-flex items-center gap-1 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={t.adopt_only}
              onChange={(e) => update(i, { adopt_only: e.target.checked })}
            />
            Adopt only
          </label>
          <div className="col-span-1">
            <Btn size="sm" variant="ghost" onClick={() => remove(i)}>
              ×
            </Btn>
          </div>
        </div>
      ))}
    </div>
  );
}

function RuleGroupEditor({
  group,
  possibleParents,
  categories,
  onChange,
}: {
  group: GroupDraft;
  possibleParents: Question[];
  categories: QuestionCategory[];
  /** null = the group emptied itself out of existence. */
  onChange: (next: GroupDraft | null) => void;
}) {
  const replaceChild = (key: string, next: CondDraft | null) => {
    const children = next
      ? group.children.map((c) => (c.key === key ? next : c))
      : group.children.filter((c) => c.key !== key);
    onChange(children.length === 0 ? null : { ...group, children });
  };
  return (
    <div className="space-y-2">
      {group.children.length > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Match</span>
          <Select
            value={group.op}
            onChange={(e) => onChange({ ...group, op: e.target.value as 'and' | 'or' })}
          >
            <option value="and">ALL</option>
            <option value="or">ANY</option>
          </Select>
          <span className="text-xs text-slate-500">of the following</span>
        </div>
      )}
      {group.children.map((child) => (
        <ConditionRowEditor
          key={child.key}
          cond={child}
          possibleParents={possibleParents}
          categories={categories}
          onChange={(next) => replaceChild(child.key, next)}
          onRemove={() => replaceChild(child.key, null)}
        />
      ))}
      <div className="flex gap-2">
        <Btn
          size="sm"
          variant="secondary"
          onClick={() => onChange({ ...group, children: [...group.children, emptyCond()] })}
        >
          + Add condition
        </Btn>
      </div>
    </div>
  );
}

// Native select popups size themselves to their widest option, so very long
// question labels are shortened in JS — ellipsising the closed control alone
// would not stop the popup (or the modal grid) from blowing out.
function truncateLabel(label: string, max = 70): string {
  return label.length > max ? `${label.slice(0, max - 1).trimEnd()}…` : label;
}

function ConditionRowEditor({
  cond,
  possibleParents,
  categories,
  onChange,
  onRemove,
}: {
  cond: CondDraft;
  possibleParents: Question[];
  categories: QuestionCategory[];
  onChange: (next: CondDraft) => void;
  onRemove: () => void;
}) {
  return (
    // items-center keeps "equals" and ✕ level with the input row instead of
    // the hint baseline that items-end was pinning them to.
    <div className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2">
      {/* min-w-0 lets the 1fr column shrink below the select's intrinsic
          (widest-option) width so the row never outgrows the modal. */}
      <div className="min-w-0">
        <Field label="Question" hint="Pick any question from any category.">
          <Select
            className="truncate"
            value={cond.parent_id ?? ''}
            onChange={(e) => onChange({ ...cond, parent_id: e.target.value || null })}
          >
            <option value="">— Pick a question —</option>
            {categories.map((cat) => {
              const inCat = possibleParents.filter((p) => p.category_id === cat.id);
              if (inCat.length === 0) return null;
              return (
                <optgroup key={cat.id} label={cat.name}>
                  {inCat.map((p) => (
                    <option key={p.id} value={p.id} title={p.label}>
                      {truncateLabel(p.label)}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </Select>
        </Field>
      </div>
      <span className="text-xs text-slate-500">equals</span>
      <div className="min-w-0">
        <Field label="Answer" hint='Use "true"/"false" for yes/no'>
          <TextInput
            value={cond.value}
            disabled={!cond.parent_id}
            onChange={(e) => onChange({ ...cond, value: e.target.value })}
          />
        </Field>
      </div>
      <Btn size="sm" variant="ghost" onClick={onRemove} aria-label="Remove condition">
        ×
      </Btn>
    </div>
  );
}

function fromQuestion(
  q: Question | undefined,
  defaultCategoryId: string,
  allQuestions: Question[] = [],
  questionnaireId = '',
): QuestionDraft {
  if (!q) return emptyDraft(defaultCategoryId, questionnaireId);
  return {
    type: q.type,
    questionnaire_id: q.questionnaire_id ?? questionnaireId,
    category_id: q.category_id,
    label: q.label,
    help_text: q.help_text ?? '',
    required: q.required,
    requires_upload: q.requires_upload,
    requires_expiry: q.requires_expiry,
    allow_multiple_files: q.allow_multiple_files,
    include_in_compliance_documents: q.include_in_compliance_documents,
    compliance_doc_label: q.compliance_doc_label ?? '',
    exclude_from_compliance: q.exclude_from_compliance,
    upload_label: q.upload_label ?? '',
    options: q.options ?? [],
    visibility: questionToRuleDraft(q),
    adoptable_doc_id: q.adoptable_doc_id,
    video_url: q.video_url ?? '',
    image_url: q.image_url ?? '',
    adoption_triggers: q.adoption_triggers.map((t) => ({
      trigger_value: t.trigger_value ?? null,
      document_id: t.document_id,
      adopt_only: t.adopt_only,
    })),
    no_followup_ids: allQuestions
      .filter((c) => c.parent_id === q.id && c.condition?.value === false)
      .map((c) => c.id),
    option_followup_ids:
      q.type === 'multiselect' || q.type === 'multiselect_uploads'
        ? Object.fromEntries(
            (q.options ?? []).map((opt) => [
              opt,
              [
                ...new Set(
                  allQuestions
                    .filter((c) => c.parent_id === q.id && c.condition?.value === opt)
                    .map((c) => c.instance_of ?? c.id),
                ),
              ],
            ]),
          )
        : {},
  };
}

function toApiPayload(d: QuestionDraft): Record<string, unknown> {
  const isDocType = d.type === 'document_adopt' || d.type === 'document_upload';
  const isSignature = d.type === 'signature';
  const isInformational = d.type === 'informational';
  const isImageInfo = d.type === 'image_info';
  const isReadOnly = isInformational || isImageInfo;
  const suppressUpload = isDocType || isSignature || isReadOnly;
  const expiryApplicable = d.type === 'document_upload' || d.type === 'multiselect_uploads';
  const payload: Record<string, unknown> = {
    type: d.type,
    questionnaire_id: d.questionnaire_id,
    category_id: d.category_id,
    label: d.label.trim(),
    help_text: d.help_text.trim() || null,
    required: d.required,
    requires_upload: suppressUpload ? false : d.requires_upload,
    requires_expiry: expiryApplicable ? d.requires_expiry : false,
    allow_multiple_files: d.allow_multiple_files,
    include_in_compliance_documents: d.include_in_compliance_documents,
    compliance_doc_label: d.include_in_compliance_documents
      ? d.compliance_doc_label.trim() || null
      : null,
    exclude_from_compliance: d.exclude_from_compliance,
    upload_label: !suppressUpload && d.requires_upload ? d.upload_label.trim() || null : null,
    options:
      d.type === 'select' || d.type === 'multiselect' || d.type === 'multiselect_uploads'
        ? d.options.map((s) => s.trim()).filter(Boolean)
        : null,
    ...(isReadOnly
      ? { parent_id: null, condition: null, visibility_rule: null }
      : draftToVisibilityPayload(d.visibility)),
    adoptable_doc_id: isDocType ? d.adoptable_doc_id : null,
    video_url: isInformational ? d.video_url.trim() || null : null,
    image_url: isImageInfo ? d.image_url.trim() || null : null,
    adoption_triggers:
      isDocType || isSignature || isReadOnly
        ? []
        : d.type === 'multiselect_uploads'
          ? // option-mapped rows still require both the option key and a doc
            d.adoption_triggers.filter((t) => t.trigger_value && t.document_id)
          : // generic rows are unconditional (trigger_value null): only a doc is required
            d.adoption_triggers.filter((t) => t.document_id),
  };
  return payload;
}

// Links an existing global category to the active questionnaire. Creating new
// categories stays in Settings; this only offers categories not already linked.
function AddCategoryToQuestionnaire({
  available,
  hasGlobalCategories,
  pending,
  onAdd,
}: {
  available: QuestionCategory[];
  hasGlobalCategories: boolean;
  pending: boolean;
  onAdd: (categoryId: string) => void;
}) {
  const [selected, setSelected] = useState('');
  if (!hasGlobalCategories) {
    return (
      <p className="text-sm text-slate-500">
        No categories exist yet.{' '}
        <Link to="/admin/settings" className="text-[#4D148C] hover:underline">
          Create categories in Settings
        </Link>
        , then add them to this questionnaire here.
      </p>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Select
        aria-label="Add an existing category to this questionnaire"
        className="sm:w-64"
        value={selected}
        onChange={(e) => setSelected(e.target.value)}
        disabled={available.length === 0 || pending}
      >
        <option value="">
          {available.length === 0 ? 'All categories already added' : '+ Add a category…'}
        </option>
        {available.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Select>
      <Btn
        size="sm"
        variant="secondary"
        loading={pending}
        disabled={!selected || pending}
        onClick={() => {
          if (!selected) return;
          onAdd(selected);
          setSelected('');
        }}
      >
        Add
      </Btn>
    </div>
  );
}

function QuestionnaireHeader({
  questionnaires,
  active,
  onSelect,
}: {
  questionnaires: Questionnaire[];
  active: Questionnaire;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{active.name}</h1>
          <div className="mt-1 text-xs text-slate-500">
            Audience:{' '}
            <span className="font-medium">
              {active.audience === 'company' ? 'vendor' : active.audience}
            </span>
            {active.contractor_types && active.contractor_types.length > 0
              ? ` · Contractor types: ${active.contractor_types.join(', ')}`
              : ' · Applies to all contractor types'}
          </div>
          {active.description && (
            <div className="mt-1 text-sm text-slate-600">{active.description}</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Field label="Editing">
            <Select value={active.id} onChange={(e) => onSelect(e.target.value)}>
              {questionnaires.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.name} ({q.audience})
                </option>
              ))}
            </Select>
          </Field>
          <Link to="/admin/questionnaires" className="text-xs text-[#4D148C] hover:underline">
            Manage →
          </Link>
        </div>
      </div>
    </div>
  );
}

function ImageInfoUploader({
  imageUrl,
  onChange,
}: {
  imageUrl: string;
  onChange: (url: string) => void;
}) {
  const [error, setError] = useState<unknown>(null);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const presigned = await adminApi.post<{ url: string; public_url: string }>(
        '/api/questions/image-upload-url',
        { file_name: file.name, mime_type: file.type },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      return presigned.public_url;
    },
    onSuccess: (publicUrl) => {
      setError(null);
      onChange(publicUrl);
    },
    onError: (err) => setError(err),
  });

  return (
    <Field
      label="Image"
      required
      hint="Shown at the top of the step. PNG, JPEG, SVG, WebP or GIF — 5 MB max."
    >
      <div className="space-y-3">
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            className="max-h-48 w-auto rounded border border-slate-200 object-contain"
          />
        )}
        <div className="flex items-center gap-3">
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadMutation.mutate(f);
              e.target.value = '';
            }}
            className="text-sm"
            disabled={uploadMutation.isPending}
          />
          {uploadMutation.isPending && <span className="text-xs text-slate-500">Uploading…</span>}
          {imageUrl && !uploadMutation.isPending && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="text-xs text-red-600 hover:underline"
            >
              Remove
            </button>
          )}
        </div>
        <ErrorMessage error={error} />
      </div>
    </Field>
  );
}

// "A" for a single link; "A, B, C" when the question also has mirror rows.
function linkedOptionsLabel(question: Question, all: Question[]): string {
  const own = String(question.condition?.value ?? '');
  const mirrorOptions = all
    .filter((x) => x.instance_of === question.id && typeof x.condition?.value === 'string')
    .map((x) => x.condition!.value as string);
  return [own, ...mirrorOptions].filter(Boolean).join(', ');
}
